import { GetLoansReturn } from 'graphql/loans/queries';
import { useGetLoans } from 'hooks/useGetLoans';
import { useUpdateEntry, useDeleteOrderLedgerAssociation } from 'hooks/ledger/mutations';
import { useDeleteLoan, useDeleteLoanInterest, useUpdateLoan } from 'hooks/loans/mutations';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { filterAccountingEntriesByLoanId } from 'utils/accounting';
import useAlert from 'utils/alert';
import { LEDGER_CODE_LOAN_AMOUNT } from 'utils/constants';
import { AccountingType, Loan } from 'v2-types/order';

import { LoanFormType, ModalData } from './types';
import { getLoanFormDataFromLoan } from './utils';
import { loanInitialValues } from './utils/loanInitialValues';
import { useGetOrderAccounting } from 'hooks/useGetOrderAccounting';
import { useTranslation } from 'react-i18next';
import { useSocketAccountingEventInvalidator } from 'hooks/useSocketAccountingEventInvalidator';
import { useInfoUpdateEvent } from 'hooks/useInfoUpdateEvent';

export const useLoansHooks = () => {
  const { id: orderId, loanId } = useParams<{ id: string; loanId?: string }>();
  const history = useHistory();
  const showAlert = useAlert();
  const [selectedLoan, setSelectedLoan] = useState<Loan | undefined>(undefined);
  const [modalData, setModalData] = useState<ModalData>();
  const loanForm = useForm<LoanFormType>({ defaultValues: loanInitialValues, mode: 'onChange' });

  const queryCache = useQueryClient();

  const { t } = useTranslation();

  useSocketAccountingEventInvalidator({
    queries: [
      ['party-order', orderId],
      ['loans', orderId],
    ],
  });

  useInfoUpdateEvent(() => {
    queryCache.invalidateQueries([
      ['party-order', orderId],
      ['loans', orderId]
    ]);
  }, (data) => ["createOrderLoan", "createOrderLoanInterest", "deleteOrderLoan", "deleteOrderLoanInterest", "updateOrderLoan"].includes(data.kind));

  const onSelectLoan = (loan: Loan | undefined, keepDirtyValues = false) => {
    if (loan) {
      const loanAmountEntry = loan.accounting.find((entry) =>
        entry.code.startsWith(LEDGER_CODE_LOAN_AMOUNT)
      );

      const { pathname } = history.location;
      history.replace(pathname.replace(/\/loans.*$/, `/loans/${loan._id}`));

      loanForm.reset(
        {
          ...loan,
          loan_number: loan.loan_number !== null ? loan.loan_number : '',
          loanAmountEntry,
          estate_id: loan.estate?._id || '',
        },
        { keepDirtyValues }
      );
      setSelectedLoan(loan);
    }
  };

  const {
    data,
    isLoading: isLoansLoading,
    isFetching: isLoansFetching,
    isError: isLoansError,
  } = useGetLoans({
    orderId,
    queryConfig: {
      onSuccess: (response) => {
        const { loans } = response.getOrder;
        let loan: Loan | undefined = response.getOrder.loans[0];
        if (loanId && loans.some((loanItem) => loanItem._id === loanId)) {
          loan = loans.find((loanItem) => loanItem._id === loanId);
        }
        onSelectLoan(loan, false);
      },
    },
  });

  const { data: orderAccountingEscrow } = useGetOrderAccounting({
    orderId,
    filters: {
      accounting: '/^(escrow_estimated|escrow_initial|escrow_monthly)$/',
    },
    queryKey: `${orderId}-accountingEscrow`,
  });
  const orderAccountingEscrowEntries = orderAccountingEscrow?.getOrder.accounting ?? [];

  const updateLoansCache = (loans: Loan[], loanIdToSelect?: string) => {
    queryCache.setQueryData<GetLoansReturn>(['loans', orderId], (dataCache) => {
      if (loanIdToSelect !== undefined) {
        const loanToSelect = loans.find((loan) => loan._id === loanIdToSelect);
        onSelectLoan(loanToSelect);
      }

      return { ...dataCache, getOrder: { ...dataCache?.getOrder, loans } } as GetLoansReturn;
    });
  };

  const { mutate: deleteLoaninterest, isLoading: isDeleteInterestLoading } = useDeleteLoanInterest({
    queryConfig: {
      onSuccess: () => {
        if (modalData?.modal === 'deleteInterest') {
          const loan = data?.getOrder.loans!.find((l) => l._id === loanId)!;
          loan.interests = loan?.interests.filter(
            (interest) => interest._id !== modalData.interestId
          );
          updateLoansCache(
            [...data?.getOrder.loans.filter((l) => l._id !== loanId)!, loan],
            loanId
          );
          setModalData({ modal: '' });
          showAlert('Interest successfully deleted', 'success');
        }
      },
    },
  });

  const { mutate: deleteLoan, isLoading: isDeleteLoanLoading } = useDeleteLoan({
    queryConfig: {
      onSuccess: (response) => {
        const loan: Loan | undefined = response.deleteOrderLoan.loans[0];
        updateLoansCache(response.deleteOrderLoan.loans, loan ? loan._id : undefined);
        setModalData({ modal: '' });
        showAlert('Loan successfully deleted', 'success');
      },
    },
  });

  const { mutate: updateLoan, isLoading: isUpdateLoanLoading } = useUpdateLoan({
    queryConfig: {
      onSuccess: (response) => {
        const updatedLoan = response.updateOrderLoan.loans.find((l) => l._id === loanId)!;
        const localLoan = data?.getOrder.loans!.find((l) => l._id === loanId);
        updateLoansCache(
          [
            ...data?.getOrder.loans!.filter((l) => l._id !== loanId)!,
            {
              ...updatedLoan,
              accounting: localLoan?.accounting!,
            },
          ],
          loanId
        );
        showAlert('Loan successfully updated', 'success');
      },
    },
  });

  const { mutate: updateEntry, isLoading: isEntryUpdateLoading } = useUpdateEntry({});

  useEffect(() => {
    if (isLoansLoading || isLoansFetching) {
      showAlert('Getting new information', 'info');
    }
  }, [isLoansLoading, isLoansFetching, showAlert]);

  const updateAccountingCache = (accounting: AccountingType[]) => {
    queryCache.setQueryData<GetLoansReturn>(['loans', orderId], (dataCache) => {
      const loans = [...(dataCache?.getOrder.loans || [])];
      const loanIndex = loans.findIndex((loan) => loan._id === selectedLoan?._id);
      if (loanIndex >= 0) {
        loans[loanIndex] = {
          ...loans[loanIndex],
          accounting: filterAccountingEntriesByLoanId(accounting, selectedLoan?._id!),
        };
      }
      return { ...dataCache, getOrder: { ...dataCache?.getOrder, loans } } as GetLoansReturn;
    });
  };

  const onClose = () => setModalData({ modal: '' });

  const { mutate: deleteAdditionalEntry, isLoading: isDeleteAdditionalLoading } =
		useDeleteOrderLedgerAssociation({
		  queryConfig: {},
		});

  const deleteAdditional = () => {
    if (modalData?.modal === 'deleteAdditional') {
      const { _id: additionalId, kind } = modalData?.entry!;
      const { _id: accountingId } = loanForm.getValues('loanAmountEntry') as AccountingType;
      deleteAdditionalEntry(
        {
          orderId,
          kind,
          accountingId,
          partyId: additionalId,
          filters: {
            accounting: `/.*${selectedLoan?._id}.*/`,
          },
        },
        {
          onSuccess: () => {
            const loanAmountEntry = loanForm.getValues('loanAmountEntry');
            if (loanAmountEntry?.involved_parties) {
              loanAmountEntry.involved_parties = loanAmountEntry?.involved_parties.filter(
                (party) => party.kind !== kind
              );
              loanForm.setValue(
                'loanAmountEntry.involved_parties',
                loanAmountEntry.involved_parties
              );
            }
            showAlert('Additional successfully deleted', 'success');
            onClose();
          },
        }
      );
    }
  };

  const loans = data?.getOrder.loans || [];
  const parties = data?.getOrder?.parties || [];
  const onDeleteLoanInterest = () => {
    if (modalData?.modal === 'deleteInterest' && selectedLoan) {
      deleteLoaninterest({
        orderId,
        loanId: selectedLoan._id,
        interestId: modalData?.interestId,
      });
    }
  };

  const onSave = loanForm.handleSubmit(async ({ loanAmountEntry, ...rest }) => {
    const { isDirty, dirtyFields } = loanForm.formState;

    if (isDirty && selectedLoan) {
      if (dirtyFields.loanAmountEntry?.amount) {
        await updateEntry({
          id: orderId,
          accountingId: loanAmountEntry?._id!,
          data: { amount_override: loanAmountEntry?.amount! },
        });
      }

      const dirtyFieldsMutable = { ...dirtyFields };
      delete dirtyFieldsMutable.loanAmountEntry;

      if (Object.keys(dirtyFieldsMutable).length > 0) {
        const loanData = getLoanFormDataFromLoan(rest as any) as Partial<Loan>;
        if (Object.keys(loanData).length > 0) {
          await updateLoan({
            orderId,
            loanId: selectedLoan._id,
            data: {
              ...loanData,
            },
          });
        }
      }
      loanForm.reset(loanForm.getValues());
    }
  });

  const onDeleteLoan = () => {
    if (selectedLoan?._id) {
      deleteLoan({
        orderId,
        loanId: selectedLoan._id,
      });
    }
  };

  return {
    loans,
    loanId,
    onSelectLoan,
    selectedLoan,
    loanForm,
    setModalData,
    modalData,
    onDeleteLoanInterest,
    isDeleteInterestLoading,
    updateLoansCache,
    onDeleteLoan,
    isDeleteLoanLoading,
    onSave,
    isUpdateLoanLoading: isUpdateLoanLoading || isEntryUpdateLoading,
    isLoansLoading,
    isLoansError,
    orderAccountingEscrowEntries,
    deleteAdditional,
    isDeleteAdditionalLoading,
    onClose,
    t,
    parties,
  };
};
